import React, { useState } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import Rating from '@mui/material/Rating';
import { StarRounded } from '@mui/icons-material';

/**
 * ⚠️ NOT A CARD, AND NOT AT THE BOTTOM. Both of those were why this was never once used.
 *
 * It sat below the note — past every section and the source documents, which on a real
 * encounter is several screens down — inside a bordered grey box that read as a footer. The
 * clinician's path through this step ends at Copy or Export and then they leave, so nothing
 * ever took them past the last section to find it.
 *
 * So it moved up under the action row, where the buttons they DO press already are, and lost
 * its chrome to earn the place: one line, no border, no fill. A second bordered box directly
 * under the buttons would have been louder than the note it is asking about.
 */

interface ChartFeedbackProps {
  defaultRating?: number | null;
  onSubmit?: (rating: number) => void | Promise<void>;
}

const ChartFeedback: React.FC<ChartFeedbackProps> = ({
  defaultRating = null,
  onSubmit,
}) => {
  const [rating, setRating] = useState<number | null>(defaultRating);
  const [submitted, setSubmitted] = useState(defaultRating !== null);

  const handleChange = (event: React.SyntheticEvent, value: number | null) => {
    const fallback = Number((event.target as HTMLInputElement | null)?.value);
    const resolved =
      typeof value === 'number' && Number.isFinite(value)
        ? value
        : Number.isFinite(fallback) && fallback > 0
          ? fallback
          : null;

    if (resolved === null) return;
    setRating(resolved);
    setSubmitted(true);
    void onSubmit?.(resolved);
  };

  // One row, ending where the buttons above it end, so it reads as the last item in that row
  // rather than as the first thing in the note. Wraps rather than squeezing on a narrow screen.
  const row = {
    direction: 'row' as const,
    alignItems: 'center',
    justifyContent: 'flex-end',
    spacing: 1,
    flexWrap: 'wrap' as const,
  };

  if (submitted) {
    return (
      <Box sx={{ mb: 3 }}>
        <Stack {...row}>
          <Rating
            value={rating}
            readOnly
            size="small"
            icon={<StarRounded fontSize="inherit" />}
            emptyIcon={<StarRounded fontSize="inherit" sx={{ opacity: 0.3 }} />}
          />
          <Typography variant="body2" color="text.secondary">
            Thanks for your feedback!
          </Typography>
          <Button size="small" onClick={() => setSubmitted(false)}>
            Change
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack {...row}>
        <Typography variant="body2" color="text.secondary">
          How helpful was this note?
        </Typography>
        <Rating
          name="chartmind-feedback"
          value={rating}
          onChange={handleChange}
          icon={<StarRounded fontSize="inherit" />}
          emptyIcon={<StarRounded fontSize="inherit" sx={{ opacity: 0.3 }} />}
        />
      </Stack>
    </Box>
  );
};

export default ChartFeedback;
